import { useEffect, useState } from 'react';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { useDebouncedValue } from '@mantine/hooks';
import { Button, Group, Popover, ScrollArea, Stack, Text, TextInput } from '@mantine/core';
import {
  ApiError,
  useAttendanceControllerStatus,
  useAttendanceControllerClock,
  useAttendanceControllerOrderWork,
  useAttendanceControllerOrderWorkStatus,
  useAuthControllerMe,
  useOrdersControllerList,
  getAttendanceControllerStatusQueryKey,
  getAttendanceControllerOrderWorkStatusQueryKey,
  type ClockDtoKind,
  type OrderWorkDtoAction,
} from '@werkbank/shared';
import { Icon } from './navIcons';
import { computeClockDisplay, hmDuration } from '../lib/clockDisplay';
import { formatTimeDE } from '../lib/format';

/**
 * P2-C: Auftragszeit für Werkstattkräfte im Web. Bei Anwesenheit lässt sich ein
 * offener Auftrag suchen und starten; läuft bereits einer, sind „Wechsel" (ein
 * Klick: STOP + neuer WORK_START) und „Ohne Auftrag" (nur STOP) möglich. Alle
 * Aktionen laufen über `/attendance/order-work`; nach jeder Aktion werden die
 * Auftragszeit- und die Anwesenheits-Statusabfrage invalidiert.
 */
function OrderWorkControl() {
  const qc = useQueryClient();
  const owStatus = useAttendanceControllerOrderWorkStatus({ query: { retry: false } });
  const [opened, setOpened] = useState(false);
  const [action, setAction] = useState<OrderWorkDtoAction>('start');
  const [search, setSearch] = useState('');

  const orderWork = useAttendanceControllerOrderWork<ApiError>({
    mutation: {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: getAttendanceControllerOrderWorkStatusQueryKey() });
        void qc.invalidateQueries({ queryKey: getAttendanceControllerStatusQueryKey() });
        setOpened(false);
        setSearch('');
      },
    },
  });

  // Offene Aufträge nur laden, während der Wähler offen ist; Status-Filter auf
  // ACTIVE/IN_PROGRESS clientseitig (der Listen-Hook kennt nur einen Status).
  // Vollanalyse S5 (M19-Muster): Suche debounced statt ein Request pro
  // Tastendruck; placeholderData hält die Trefferliste beim Tippen stehen.
  const [debouncedSearch] = useDebouncedValue(search.trim(), 250);
  const orders = useOrdersControllerList(
    { search: debouncedSearch || undefined },
    { query: { enabled: opened, retry: false, placeholderData: keepPreviousData } },
  );
  const openOrders = (orders.data ?? []).filter(
    (o) => o.status === 'ACTIVE' || o.status === 'IN_PROGRESS',
  );

  const current = owStatus.data?.current ?? null;
  const openPicker = (next: OrderWorkDtoAction) => {
    setAction(next);
    setSearch('');
    setOpened(true);
  };
  const pick = (orderId: string) => orderWork.mutate({ data: { action, orderId } });

  const picker = (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-end"
      width={300}
      withArrow
      trapFocus
    >
      <Popover.Target>
        <Button
          size="xs"
          variant="light"
          leftSection={<Icon name="orders" size={16} />}
          onClick={() => openPicker(current ? 'switch' : 'start')}
          data-testid={current ? 'order-work-switch' : 'order-work-start'}
        >
          {current ? 'Wechsel' : 'An Auftrag arbeiten'}
        </Button>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="xs">
          <TextInput
            size="xs"
            placeholder="Auftrag suchen…"
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            data-autofocus
            data-testid="order-work-search"
          />
          <ScrollArea.Autosize mah={220}>
            <Stack gap={4}>
              {openOrders.length === 0 && (
                <Text size="xs" c="dimmed" py={4}>
                  {orders.isFetching ? 'Suche…' : 'Keine offenen Aufträge'}
                </Text>
              )}
              {openOrders.map((o) => (
                <Button
                  key={o.id}
                  size="xs"
                  variant="subtle"
                  justify="flex-start"
                  loading={orderWork.isPending}
                  onClick={() => pick(o.id)}
                  data-testid="order-work-option"
                >
                  <Text size="xs" fw={600} span>
                    {o.number}
                  </Text>
                  <Text size="xs" c="dimmed" span ml={6} lineClamp={1}>
                    {o.title}
                  </Text>
                </Button>
              ))}
            </Stack>
          </ScrollArea.Autosize>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );

  if (current) {
    return (
      <Group gap="xs" wrap="nowrap" data-testid="order-work">
        <Text size="xs" c="dimmed" className="wb-tnum" data-testid="order-work-current">
          an {current.number} seit {formatTimeDE(current.sinceIso)}
        </Text>
        {picker}
        <Button
          size="xs"
          variant="default"
          loading={orderWork.isPending}
          onClick={() => orderWork.mutate({ data: { action: 'stop' } })}
          data-testid="order-work-stop"
        >
          Ohne Auftrag
        </Button>
      </Group>
    );
  }
  return (
    <Group gap="xs" wrap="nowrap" data-testid="order-work">
      {picker}
    </Group>
  );
}

/**
 * Stempeluhr im Kopfbereich (Phase 3, 4.6 v1.1; TICKET P3-A2). Drei Zustände mit
 * aktiver Pausenerfassung und live hochzählender Dauer (Minutentakt, kein
 * API-Polling): Kommen → Pause/Gehen → Pause beenden/Gehen. Die Arbeitszeit ist
 * netto (gestempelte Pausen abgezogen) und steht im Pausenzustand still.
 */
export function ClockWidget() {
  const qc = useQueryClient();
  // Nutzer ohne Arbeitszeiterfassung (Faruq 2026-08-18) sehen keine Stempeluhr.
  const me = useAuthControllerMe({ query: { retry: false } });
  const status = useAttendanceControllerStatus({ query: { retry: false } });
  const clock = useAttendanceControllerClock<ApiError>({
    mutation: {
      onSuccess: () =>
        void qc.invalidateQueries({ queryKey: getAttendanceControllerStatusQueryKey() }),
    },
  });

  // Minutentakt clientseitig (30 s reicht für eine Minutenanzeige, kein Sekundentakt).
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  if (me.data?.timeTrackingDisabled) return null;
  if (!status.data) return null;
  const s = status.data;
  const disp = computeClockDisplay(s, nowMs);
  const doClock = (kind: ClockDtoKind) => clock.mutate({ data: { kind } });
  const sinceTime = s.since
    ? new Date(s.since).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
    : null;
  const dotColor =
    s.state === 'PRESENT'
      ? 'var(--wb-status-paid)'
      : s.state === 'BREAK'
        ? 'var(--wb-status-due)'
        : 'var(--wb-text-faint)';

  let main: string;
  let sub: string | null;
  if (s.state === 'PRESENT') {
    main = `Arbeitszeit ${hmDuration(disp.netMinutes)}`;
    sub = `seit ${sinceTime} · Pause heute ${hmDuration(disp.breakTotalMinutes)}`;
  } else if (s.state === 'BREAK') {
    main = `Pause ${hmDuration(disp.currentBreakMinutes)}`;
    sub = `seit ${sinceTime} · Arbeitszeit ${hmDuration(disp.netMinutes)}`;
  } else {
    // D-W1 Welle 2 (QS-7, W2-B3): „Nicht angemeldet" las sich neben dem
    // Avatar wie ein Login-Problem — gemeint ist die Stempeluhr.
    main = 'Nicht eingestempelt';
    sub = s.workedMinutes > 0 ? `Heute erfasst: ${hmDuration(s.workedMinutes)}` : null;
  }

  return (
    <Group gap="sm" wrap="nowrap" data-testid="clock-widget">
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: dotColor,
          display: 'inline-block',
          flex: 'none',
        }}
      />
      <Stack gap={0} style={{ minWidth: 0 }}>
        <Group gap={6} wrap="nowrap">
          <Text size="sm" fw={500} className="wb-tnum" data-testid="clock-main">
            {main}
          </Text>
          {disp.hint && (
            <Text
              size="xs"
              fw={600}
              c={disp.hint.level === 'red' ? 'red' : 'orange'}
              data-testid="clock-hint"
            >
              {disp.hint.text}
            </Text>
          )}
        </Group>
        {sub && (
          <Text size="xs" c="dimmed" className="wb-tnum" data-testid="clock-sub">
            {sub}
          </Text>
        )}
      </Stack>

      {s.state === 'OUT' && (
        <Button
          size="xs"
          variant="light"
          leftSection={<Icon name="clockPlay" size={16} />}
          loading={clock.isPending}
          onClick={() => doClock('IN')}
          data-testid="clock-in"
        >
          Kommen
        </Button>
      )}
      {s.state === 'PRESENT' && (
        <>
          <Button
            size="xs"
            variant="default"
            leftSection={<Icon name="clockPause" size={16} />}
            loading={clock.isPending}
            onClick={() => doClock('BREAK_START')}
            data-testid="clock-break-start"
          >
            Pause
          </Button>
          <Button
            size="xs"
            variant="default"
            leftSection={<Icon name="clockStop" size={16} />}
            loading={clock.isPending}
            onClick={() => doClock('OUT')}
            data-testid="clock-out"
          >
            Gehen
          </Button>
          {/* P2-C: Auftragszeit nur bei Anwesenheit (nicht in der Pause). */}
          <OrderWorkControl />
        </>
      )}
      {s.state === 'BREAK' && (
        <>
          <Button
            size="xs"
            variant="light"
            leftSection={<Icon name="clockResume" size={16} />}
            loading={clock.isPending}
            onClick={() => doClock('BREAK_END')}
            data-testid="clock-break-end"
          >
            Pause beenden
          </Button>
          <Button
            size="xs"
            variant="default"
            leftSection={<Icon name="clockStop" size={16} />}
            loading={clock.isPending}
            onClick={() => doClock('OUT')}
            data-testid="clock-out"
          >
            Gehen
          </Button>
        </>
      )}
    </Group>
  );
}
